import type { ReactElement } from "react";
import { ArrowUpRight } from "lucide-react";
import type { WidgetBodyProps } from "../types.js";
import { useWidgetActions } from "../widget-actions.js";
import type { QuickAccessView } from "./types.js";

/**
 * Data-blind module launcher. It receives no route or URL; clicking passes an
 * opaque module id back to the shell, which revalidates it before navigating.
 */
export function QuickAccessWidget({
  data,
}: WidgetBodyProps<QuickAccessView>): ReactElement {
  const openModule = useWidgetActions()?.openModule;

  if (data.modules.length === 0) {
    return (
      <p className="text-dense-1 italic text-pb-text-muted">
        No workspace modules are enabled.
      </p>
    );
  }

  return (
    <ul className="-mx-2 -my-1 divide-y divide-pb-border/60">
      {data.modules.map((module) => {
        const Icon = module.icon;
        return (
          <li key={module.id}>
            {/* eslint-disable-next-line ds/no-hand-built -- kept: a clickable row with its own inset and a pulled-out hover; ListItem has no inline padding and its own row layout, which AP-07's selection fill does not cover. */}
            <button
              type="button"
              disabled={!openModule}
              onClick={() => openModule?.(module.id)}
              className="group flex w-full items-center gap-3 rounded-md px-2 py-2.5 text-start transition-colors hover:pb-overlay-paint focus-ring disabled:cursor-default disabled:opacity-70"
            >
              <span
                className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-pb-card-elevated ${module.accent}`}
              >
                <Icon className="h-4 w-4" aria-hidden />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-medium text-pb-text">
                  {module.name}
                </span>
                <span className="block truncate text-xs text-pb-text-muted">
                  {module.blurb}
                </span>
              </span>
              <ArrowUpRight
                className="h-3.5 w-3.5 shrink-0 text-pb-text-muted transition-colors group-hover:text-pb-text"
                aria-hidden
              />
            </button>
          </li>
        );
      })}
    </ul>
  );
}
